import { hasAvailableVariant } from '@theme/produktfinder-variants'

const PROXY = '/apps/api/produktfinder'
const STATUS_STEPS = [
  'Ich lese dein Anliegen…',
  'Verstehe, worum es dir geht…',
  'Durchforste das ganze Sortiment…',
  'Vergleiche Wirkstoffe und Anwendungen…',
  'Prüfe Verfügbarkeit und Größen…',
  'Wäge die besten Treffer ab…',
  'Stelle deine Empfehlung zusammen…',
]
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

// Stable per-browser id so the hub can rate-limit per visitor (through Shopify's
// app proxy it can't see the real IP). Null in private mode → hub falls back.
function clientId() {
  try {
    let id = localStorage.getItem('pf_cid')
    if (!id) {
      id =
        window.crypto && crypto.randomUUID
          ? crypto.randomUUID()
          : 'pf-' + Math.random().toString(36).slice(2) + Date.now().toString(36)
      localStorage.setItem('pf_cid', id)
    }
    return id
  } catch {
    return null
  }
}

function init(root) {
  const form = root.querySelector('[data-produktfinder-form]')
  const input = root.querySelector('[data-produktfinder-input]')
  const submit = root.querySelector('[data-produktfinder-submit]')
  const submitLabel = submit.textContent.trim() // configured per placement
  const results = root.querySelector('[data-produktfinder-results]')
  const thinkingTpl = root.querySelector('[data-produktfinder-thinking]')
  const capWidget = root.querySelector('cap-widget')
  const capTrigger = root.querySelector('[data-produktfinder-cap-trigger]')
  let busy = false
  let statusTimer = null

  // Drop a question into the field and search — shared by the pill fallback
  // and the rotating suggestion line.
  function fillAndRun(text) {
    input.value = text
    run()
  }

  const chips = root.querySelectorAll('[data-produktfinder-chip]')
  chips.forEach((chip) => {
    chip.addEventListener('click', () => fillAndRun(chip.textContent.trim()))
  })
  buildSuggest()

  form.addEventListener('submit', (e) => {
    e.preventDefault()
    run()
  })

  // Progressive enhancement: swap the static pill row for one rotating,
  // tappable suggestion. Left untouched (pills stay) when reduced motion is on
  // or there's nothing to rotate — so JS-off and a11y users keep working chips.
  function buildSuggest() {
    const row = root.querySelector('.produktfinder__chips')
    if (!row || reduceMotion || root.querySelector('.produktfinder__suggest')) return
    const texts = Array.from(chips, (c) => c.textContent.trim()).filter(Boolean)
    if (texts.length === 0) return

    const btn = document.createElement('button')
    btn.type = 'button'
    btn.className = 'produktfinder__suggest'
    btn.innerHTML =
      '<span class="produktfinder__suggest-lead">Frag z. B.</span>' +
      '<span class="produktfinder__suggest-view"><span class="produktfinder__suggest-text"></span></span>'
    const textEl = btn.querySelector('.produktfinder__suggest-text')

    let idx = 0
    textEl.textContent = texts[idx]
    btn.addEventListener('click', () => fillAndRun(texts[idx]))
    row.style.display = 'none' // inline beats the .produktfinder__chips flex rule
    row.after(btn)

    if (texts.length === 1) return // nothing to rotate

    const DURATION = 350 // must match the CSS transition on .produktfinder__suggest-text
    function rotate() {
      const next = (idx + 1) % texts.length
      textEl.classList.add('is-out') // slide current up + fade
      setTimeout(() => {
        textEl.classList.add('is-instant') // jump, no transition
        textEl.textContent = texts[next]
        textEl.classList.remove('is-out')
        textEl.classList.add('is-below') // park below, hidden
        void textEl.offsetWidth // reflow so the next class change animates
        textEl.classList.remove('is-instant', 'is-below') // rise into place
        idx = next
      }, DURATION)
    }

    let paused = false
    let visible = true
    const tick = () => {
      if (!paused && visible) rotate()
    }
    const timer = setInterval(tick, 2000)
    const pause = () => {
      paused = true
    }
    const resume = () => {
      paused = false
    }
    btn.addEventListener('mouseenter', pause)
    btn.addEventListener('mouseleave', resume)
    btn.addEventListener('focus', pause)
    btn.addEventListener('blur', resume)
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(([e]) => {
        visible = e.isIntersecting
      }).observe(btn)
    }
    // Clean up if the section is removed/reloaded in the theme editor.
    root.addEventListener('shopify:section:unload', () => clearInterval(timer), { once: true })
  }

  // Dev preview: ?pf-preview=loading holds the thinking state (no submit).
  if (new URLSearchParams(location.search).get('pf-preview') === 'loading') {
    showThinking()
  }
  window.produktfinderPreview = showThinking // console helper: produktfinderPreview()

  async function run(capToken) {
    const wish = input.value.trim()
    if (!wish || busy) return
    busy = true
    submit.disabled = true
    submit.textContent = 'Suche…'
    showThinking()
    try {
      const res = await fetch(PROXY, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wish, cid: clientId(), ...(capToken ? { cap_token: capToken } : {}) }),
      })
      const html = await res.text()
      stopThinking()
      results.innerHTML = extractResults(html)
      if (results.querySelector('[data-produktfinder-captcha]')) {
        // hub asked for a captcha (busy IP) — show the note, open the widget,
        // and retry the search once it's solved.
        reveal(results)
        requireCaptcha()
        return
      }
      dropOutOfStock(results)
      reveal(results)
      wireCart(results)
    } catch (err) {
      stopThinking()
      results.innerHTML =
        '<div class="produktfinder-results" data-produktfinder-empty><p class="produktfinder-note">Es ist ein Fehler aufgetreten. Bitte versuch es später erneut.</p></div>'
    } finally {
      busy = false
      submit.disabled = false
      submit.textContent = submitLabel
    }
  }

  // Open the Cap captcha; on solve, retry the search with the token.
  function requireCaptcha() {
    if (!capWidget || !capTrigger) return
    const onSolve = (e) => {
      capWidget.removeEventListener('solve', onSolve)
      const token = e.detail && e.detail.token
      if (token) run(token)
    }
    capWidget.addEventListener('solve', onSolve)
    capTrigger.click()
  }

  function showThinking() {
    results.innerHTML = ''
    results.appendChild(thinkingTpl.content.cloneNode(true))
    const status = results.querySelector('[data-produktfinder-status]')
    if (!status || reduceMotion) return
    let i = 0
    statusTimer = setInterval(() => {
      i = (i + 1) % STATUS_STEPS.length
      status.textContent = STATUS_STEPS[i]
    }, 1300)
  }
  function stopThinking() {
    if (statusTimer) clearInterval(statusTimer)
    statusTimer = null
  }
}

// The app-proxy may return the fragment wrapped in the full theme layout
// (when the hub's Liquid omits `{% layout none %}`). Pull out just the
// results block so we never inject a second header/announcement bar. Falls
// back to the raw HTML if no results block is found.
function extractResults(html) {
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html')
    const node = doc.querySelector('.produktfinder-results')
    if (node) return node.outerHTML
  } catch (err) {
    /* fall through to raw html */
  }
  return html
}

// Remove any result card whose product is out of stock (no purchasable
// variant), before the reveal so OOS items never flash in. Reads only the
// `available` boolean already in each card's variants blob — the stock count
// is never sent to the client. If every card was out of stock, show the empty
// note instead of a bare intro + "alle" button.
function dropOutOfStock(results) {
  const cards = results.querySelectorAll('.produktfinder-card')
  if (cards.length === 0) return // a note/empty/captcha response — leave it as-is
  cards.forEach((card) => {
    const blob = card.querySelector('.produktfinder-variants')
    if (!blob) return // can't tell → keep it
    let variants
    try {
      variants = JSON.parse(blob.textContent)
    } catch {
      return // unparseable → keep it
    }
    if (!hasAvailableVariant(variants)) card.remove()
  })
  if (!results.querySelector('.produktfinder-card')) {
    results.innerHTML =
      '<div class="produktfinder-results" data-produktfinder-empty><p class="produktfinder-note">Dazu ist gerade nichts auf Lager. Beschreib dein Anliegen anders oder schau später nochmal vorbei.</p></div>'
  }
}

// Stagger-reveal the injected cards + typewriter the intro.
function reveal(results) {
  const intro = results.querySelector('.produktfinder-intro')
  const cards = results.querySelectorAll('.produktfinder-card')
  if (reduceMotion) {
    cards.forEach((c) => c.classList.add('is-in'))
    return
  }
  if (intro) typewriter(intro)
  cards.forEach((card, i) => {
    setTimeout(() => card.classList.add('is-in'), 120 + i * 90)
  })
}
function typewriter(el) {
  const text = el.textContent
  el.textContent = ''
  let i = 0
  const tick = () => {
    el.textContent = text.slice(0, ++i)
    if (i < text.length) setTimeout(tick, 16)
  }
  tick()
}

// Cart wiring is in Task 4 (wireCart). Exported for that task to attach.
export function wireCart(results) {
  if (window.__produktfinderWireCart) window.__produktfinderWireCart(results)
}

document.querySelectorAll('[data-produktfinder]').forEach(init)

// Theme editor: a section added or reordered is injected without a full reload,
// so wire up any Produktfinder it contains.
document.addEventListener('shopify:section:load', (e) => {
  e.target.querySelectorAll('[data-produktfinder]').forEach(init)
})
